import { flow, makeAutoObservable } from 'mobx'
import { scenarios } from '../constants/scenarios'
import { devLog } from '@methods/devLog'
import { ContextData, ContextNames, getOnboardingContext } from '@methods/getOnboardingContext'
import { RootStore } from './Root.store'
import { Language } from '@/types/translations'
import { isLocalDev } from '@/constants/envs'

// #TODO: Update the list to match BE or remove completely
export type ConfigurationNames =
  | 'ikano.de'
  | 'ikano.de.fleks'
  | 'ikano.de.b'
  | 'ikano.de.videoid'
  | 'ikano.de.addressVerification'
  | 'ikano.de.mock' // to implement
  | 'ikano.de.goToMobile'
  | 'ikano.de.goToMobile.mock'
  | 'ikano.addressVerification.test'
  | 'signicat.addressVerification.test'
  | 'bmw.eu'
  | 'bmw.pl'
  | 'bmw.de'
  | 'bmw.fr'
  | 'bmw.gr'
  | 'santander.it'
  | 'amex.great'
  | 'amex-great-demo.readid'
  | 'amex-great-demo.readid+address'
  | 'amex-great-demo.nbid+address'
  | 'amex-great-demo.sbid+address'
  | 'amex-great-demo.videoId+address'
  | 'amex-great-demo.videoId'
  | 'amex-great-demo.qes+address'
  | 'amex-great-demo.qes'
  | 'amex-great-demo.wallet+address'
  | 'amex-great-demo.wallet'
  | 'dtp.openBankingLogin'
  | 'dtp.openBankingLogin.waiting'
  | 'payment.authorization'

// #TODO: Update the list to match BE or remove completely
export type ScenarioNames =
  | 'videoId'
  | 'micropayment'
  | 'address-verification'
  | 'utility-bill-address-verification'
  | 'residence-permit-address-verification'
  | 'micropayment-fintec'
  | 'kontomatik' //OK
  | 'dev'
  | 'mock'
  | 'init'
  | 'pooling'
  | 'switch-to-mobile'
  | 'continue-on-mobile'
  | 'eid-retry'

// #TODO: probably could be removed
const contextMap = {
  // MAP OF MANGLING CONFIGURATION ID WITH SCENARIO NAMES
  videoId: 'videoId',
  videoIdForStandardSignature: 'videoId',
  signature: 'videoId',
  'address-verification': 'address-verification',
  'utility-bill-address-verification': 'address-verification',
  'residence-permit-address-verification': 'address-verification',
  micropayment: 'micropayment-fintec',
  'micropayment-fintec': 'micropayment-fintec',
  kontomatik: 'kontomatik',
  onboarding: 'onboarding',
}

const SCENARIOS_WITHOUT_BASIC_CONTEXT = [
  'continue-on-mobile',
  'verified',
  'verified-ses',
  'verified-affordability-check',
  'welcome',
  'signing-select-method',
  'ais-complete',
  'eid-complete',
  'eid-complete-cmc',
  'success',
  'signing-waiting',
  'verified-santander',
  'onventis-verification-pending',
  'error-user-sign-rejection',
  'transaction-completed',
  'readid-completed',
  // The GoCardless return page can be opened in a browser that never held the
  // onboarding session cookie (the bank's app hands off to the system browser),
  // so the basic context call would only 401. The page does not read the
  // context: its scenario has no per-configuration variant and language and
  // themeId come from the return URL. sc-21124
  'gocardless-return',
  // Reached from the return page (or the backend's redirect) in that same browser, with only the
  // GoCardless session cookie; language and themeId come from the URL as well.
  'gocardless-income-confirmation',
]

export class ScenarioStore {
  constructor(rootStore: RootStore) {
    makeAutoObservable(this)
    this.rootStore = rootStore
    this.setupPopstateListener()
  }

  private setupPopstateListener(): void {
    window.addEventListener('popstate', () => {
      // Whatever entry the browser restored, the page was reached through history.
      this.arrivedByHistoryNavigation = true

      const urlParams = new URLSearchParams(window.location.search)
      const stepParam = urlParams.get('step')

      // An entry without `step` is the one the scenario started on, before step 0 was written to
      // it (nothing else drops the param): page 0.
      const step = stepParam ? parseInt(stepParam, 10) : 0
      if (!isNaN(step) && step >= 0 && step < this.currentScenarioPages.length) {
        this.currentPageIndex = step
      }
    })
  }

  rootStore
  scenarioId: ScenarioNames = 'init'
  context: ContextData | undefined = undefined
  currentScenarioId = 'init'
  currentPageIndex = 0
  currentScenarioPages = scenarios.init
  env: string | undefined = undefined
  // True while the current page was reached through the browser's history (back/forward), false
  // after a page load or a navigation the app made itself. Lets a page that auto-forwards on mount
  // (gocardless step 0) tell a back navigation from a fresh start, instead of pushing forward again.
  arrivedByHistoryNavigation = false

  setScenarioId(scenarioId: ScenarioNames): void {
    this.scenarioId = scenarioId
  }

  setCurrentPage(page: number): void {
    // Updates step search param with pushing new history entry
    // providing proper browser history handling
    if (page < 0 || page >= this.currentScenarioPages.length) {
      devLog('Invalid page index:', page)
      return
    }

    this.arrivedByHistoryNavigation = false
    this.currentPageIndex = page

    const currentUrl = new URL(window.location.href)
    currentUrl.searchParams.set('step', page.toString())
    window.history.pushState({}, '', currentUrl.toString())
  }

  prevPage(): void {
    // Simply go back in browser history using native method
    window.history.back()
  }

  nextPage(): void {
    this.setCurrentPage(this.currentPageIndex + 1)
  }

  skipStep(): void {
    // Updates step search param with replacing current history entry,
    // so when user clicks back button,
    // they go to the previous step,
    // not the skipped one.
    const page = this.currentPageIndex + 1
    if (page < 0 || page >= this.currentScenarioPages.length) {
      devLog('Invalid page index:', page)
      return
    }

    // Don't update currentPageIndex here - let replaceState and popstate handle it
    const currentUrl = new URL(window.location.href)
    currentUrl.searchParams.set('step', page.toString())
    window.history.replaceState({}, '', currentUrl.toString())

    // Now update the index after history is updated
    this.arrivedByHistoryNavigation = false
    this.currentPageIndex = page
  }

  init(scenarioId: ScenarioNames): void {
    this.setScenarioId(scenarioId)

    if (!isLocalDev) {
      this.initPageIndexFromUrl()
    }

    this.initContext()
  }

  setEnv(env: string): void {
    this.env = env
  }

  setContext(context: ContextData): void {
    this.context = { ...this.context, ...context }
  }

  setScenarioSteps(): void {
    // language from context and query params
    if (this.context && this.context.language) {
      devLog('Setting translations from context: ', this.context.language)
      this.rootStore.TranslationsState.setTranslations(
        this.context.language as Language,
        this.context.configurationId
      )
    }

    let name = `${this.scenarioId}/${
      this.context && this.context.configurationId ? this.context.configurationId : ''
    }`
    let scenario = scenarios[name as keyof typeof scenarios]

    devLog('Scenario: ', name, scenario)

    if (!scenario) {
      name = `${this.scenarioId}`
      scenario = scenarios[name as keyof typeof scenarios]

      devLog('Scenario fallback: ', name, scenario)
    }

    this.currentScenarioPages = scenario
    this.currentScenarioId = name

    devLog(
      `Initialized Onboarding Flow\n`,
      'ConfigurationId: ',
      this.context && this.context.configurationId ? this.context.configurationId : 'null',
      '\n',
      'Context: ',
      this.context,
      '\n',
      'Current Scenario Id: ',
      this.currentScenarioId,
      '\n',
      'Pages: ',
      this.currentScenarioPages
    )
  }

  initContext = flow(function* (this: ScenarioStore) {
    try {
      if (!SCENARIOS_WITHOUT_BASIC_CONTEXT.includes(this.scenarioId)) {
        const baseContext = yield getOnboardingContext('onboarding')
        this.context = { ...this.context, ...baseContext }
      }

      if (contextMap[this.scenarioId as keyof typeof contextMap]) {
        const scenarioContext = yield getOnboardingContext(
          contextMap[this.scenarioId as keyof typeof contextMap] as ContextNames
        )
        this.context = { ...this.context, ...scenarioContext }

        if (scenarioContext && scenarioContext.env) {
          this.env = scenarioContext.env
        }
      }

      //videoId env and flags in components point to context in this store

      this.setScenarioSteps()

      this.rootStore.TrackingState.initKeepSessionAliveInterval()
    } catch (error) {
      devLog(error)
    }
  })

  initPageIndexFromUrl(): void {
    // Initialize current page index from "step" URL parameter,
    // when user loads the page for the first time or refreshes it.
    const urlParams = new URLSearchParams(window.location.search)
    const stepParam = urlParams.get('step')
    this.arrivedByHistoryNavigation = false

    if (stepParam) {
      const step = parseInt(stepParam, 10)
      if (!isNaN(step)) {
        this.currentPageIndex = step
      }
    } else {
      this.currentPageIndex = 0
      const currentUrl = new URL(window.location.href)
      currentUrl.searchParams.set('step', '0')
      window.history.replaceState({}, '', currentUrl.toString())
    }
  }
}
